import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FieldArrayType, FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';

@Component({
    selector: 'repeat-signal-material',
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, FormlyModule],
    styleUrls: ['../../config-page/config-page.component.scss'],
    template: `
    <div>
    <div class="repeat-toolbar">
    <div class="search-block">
        <label class="material-underline-label">Cerca...</label>
        <input
        class=""
        placeholder="Cerca..."
        [formControl]="searchControl"
        (input)="filterFields()"
        autocomplete="off"
        />
    </div>
    <div class="page-size-block">
        <label for="pageSize" class="material-underline-label mr-2">Righe per pagina:</label>
        <select id="pageSize" [(ngModel)]="pageSize" (change)="goToPage(1)">
        <option [value]="5">5</option>
        <option [value]="10">10</option>
        </select>
    </div>
    </div>

      <div *ngFor="let fieldGroup of pagedFieldGroup(); let i = index" class="repeat-row-card">
        <div class="flex flex-row items-start gap-4">
          <ng-container *ngFor="let f of fieldGroup.fieldGroup">
            <formly-field *ngIf="f.key !== 'signalLevels'" [field]="f"
              [ngClass]="{
                'flex-1 min-w-[180px] max-w-xs': f.type !== 'checkbox',
                'flex-none w-auto self-center': f.type === 'checkbox'
              }">
            </formly-field>
          </ng-container>
          <button type="button" (click)="remove(i + (currentPage-1)*pageSize)"
            class="flex-shrink-0 flex items-center justify-center mt-2 bg-transparent border-none w-8 h-8 transition"
            aria-label="Rimuovi segnale">
            <span class="material-icons text-red-700 hover:text-red-900">delete</span>
          </button>
        </div>
        <div class="w-1/2">
          <ng-container *ngFor="let f of fieldGroup.fieldGroup">
            <formly-field *ngIf="f.key === 'signalLevels'" [field]="f"></formly-field>
          </ng-container>
        </div>
      </div>

      <div class="pagination-container">
        <button
            type="button"
            class="pagination-btn"
            [disabled]="currentPage === 1"
            (click)="goToPage(currentPage-1)">
            <span class="material-icons">chevron_left</span>
        </button>
        <span class="pagination-label">Pagina {{currentPage}} di {{totalPages()}}</span>
        <button
            type="button"
            class="pagination-btn"
            [disabled]="currentPage === totalPages()"
            (click)="goToPage(currentPage+1)">
            <span class="material-icons">chevron_right</span>
        </button>
    </div>

      <div class="w-full flex justify-end">
        <button
          type="button"
          class="mt-2 flex items-center gap-2 bg-transparent border-none text-black hover:text-gray-600 w-auto transition px-3 py-1"
          (click)="add()"
          aria-label="Aggiungi segnale"
        >
          <span class="text-xl leading-none">+</span>
          <span>{{ props.addText }}</span>
        </button>
      </div>
      <div *ngIf="validationErrorMessage" class="text-red-500 text-sm">
        {{ validationErrorMessage }}
      </div>
    </div>
  `
})
export class RepeatSignalMaterialComponent extends FieldArrayType {
    validationErrorMessage: string | null = null;
    searchControl = new FormControl('');
    filteredFieldGroup: any[] = [];
    pageSize = 5;
    currentPage = 1;

    ngOnInit() {
        this.checkAllRowsValid();
        this.filterFields();
    }

    add() {
        if (this.checkAllRowsValid()) {
            super.add();
            this.filterFields();
            // Vai all'ultima pagina dopo l'aggiunta
            this.goToPage(this.totalPages());
        }
    }

    remove(i: number) {
        super.remove(i);
        this.filterFields();
        this.checkAllRowsValid();
    }

    checkAllRowsValid(): boolean {
        if (!this.field.fieldGroup || this.field.fieldGroup.length === 0) {
            this.validationErrorMessage = null;
            return true;
        }

        const isValid = this.field.fieldGroup.every(row => {
            if (!row.fieldGroup) return false;
            return row.fieldGroup.every(innerField => innerField.formControl?.valid);
        });

        if (!isValid) {
            this.validationErrorMessage = 'Compila tutti i campi obbligatori prima di aggiungere una nuova riga.';
        } else {
            this.validationErrorMessage = null;
        }

        return isValid;
    }

    isFieldInvalid(field: FormlyFieldConfig): boolean {
        return !!(field.formControl?.invalid && field.formControl?.touched);
    }

    filterFields() {
        const searchTerm = this.searchControl.value?.toLowerCase() || '';
        if (!this.field.fieldGroup) {
            this.filteredFieldGroup = [];
            return;
        }
        if (!searchTerm) {
            this.filteredFieldGroup = [...this.field.fieldGroup];
        } else {
            this.filteredFieldGroup = this.field.fieldGroup.filter(row => {
                if (!row.fieldGroup) return false;
                return row.fieldGroup.some(innerField => {
                    if (innerField.key === 'Name' || innerField.key === 'description' || innerField.key === 'Unit') {
                        return innerField.formControl?.value?.toLowerCase().includes(searchTerm);
                    }
                    if (innerField.key === 'SignalType') {
                        const options = innerField.templateOptions?.options;
                        if (Array.isArray(options)) {
                            return options.some((option: any) =>
                                option.label?.toLowerCase().includes(searchTerm)
                            );
                        }
                        return false;
                    }
                    return false;
                });
            });
        }
        this.goToPage(1);
    }

    pagedFieldGroup() {
        const start = (this.currentPage - 1) * this.pageSize;
        return this.filteredFieldGroup.slice(start, start + this.pageSize);
    }

    totalPages() {
        return Math.max(1, Math.ceil(this.filteredFieldGroup.length / this.pageSize));
    }

    goToPage(page: number) {
        this.currentPage = Math.max(1, Math.min(page, this.totalPages()));
    }
}
